import React, { useEffect, useState, useRef } from "react";
import MDBox from "components/MDBox";
import {
  InputBase,
  IconButton,
  Button,
  List,
  ListItem,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useSelector, useDispatch } from "react-redux";
import { fetchGroups } from "features/management/vehicleGroup/groupsThunks";
import { setSearch } from "features/management/vehicleGroup/groupsSlice";
import CreateGroupDialog from "./CreateGroupDialog";

function FilterCard() {
  const dispatch = useDispatch();
  const { groups, loading, error } = useSelector((state) => state.groups);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [openCreate, setOpenCreate] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // const debounce = useCallback((func, wait) => {
  //   let timeout;
  //   return function executedFunction(...args) {
  //     const later = () => {
  //       clearTimeout(timeout);
  //       func(...args);
  //     };
  //     clearTimeout(timeout);
  //     timeout = setTimeout(later, wait);
  //   };
  // }, []);

  const filteredGroups =
    groups?.filter((group) =>
      group.groupName?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const handleSelectGroup = (name) => {
    setSearchTerm(name);
    dispatch(setSearch(name));
    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex < filteredGroups.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && filteredGroups[selectedIndex]) {
          handleSelectGroup(filteredGroups[selectedIndex].groupName);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={1.5}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        position: "relative",
        boxShadow: "0 3px 6px rgba(0,0,0,0.08)",
      }}
    >
      {/* <MDBox
        ref={wrapperRef}
        display="flex"
        alignItems="center"
        sx={{
          border: "1px solid #d6d6d6",
          borderRadius: "10px",
          padding: "6px 12px",
          flex: 1,
          maxWidth: "360px",
          position: "relative",
          transition: "all 0.25s ease",
          "&:focus-within": {
            borderColor: "#1eb9d8",
            boxShadow: "0 0 6px rgba(30, 185, 216, 0.25)",
          },
        }}
      >
        <InputBase
          placeholder="Search groups..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          fullWidth
          sx={{
            ml: 1,
            flex: 1,
            fontSize: "0.95rem",
            "& input": {
              padding: "6px 0",
            },
          }}
          inputProps={{
            "aria-label": "Search groups",
            "aria-expanded": showDropdown,
            "aria-haspopup": "listbox",
            role: "combobox",
          }}
        />
        <IconButton
          size="small"
          sx={{
            p: "6px",
            color: "#1eb9d8",
            transition: "background 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(30,185,216,0.08)",
            },
          }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>

        {showDropdown && (
          <Paper
            sx={{
              position: "absolute",
              top: "110%",
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: 260,
              overflowY: "auto",
              mt: 0.5,
              boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
              borderRadius: "10px",
              border: "1px solid #f0f0f0",
            }}
            role="listbox"
            aria-label="Group options"
          >
            <List sx={{ py: 0 }}>
              {loading ? (
                <ListItem sx={{ justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading groups...
                  </Typography>
                </ListItem>
              ) : error ? (
                <ListItem>
                  <Typography variant="body2" color="error">
                    Error loading groups
                  </Typography>
                </ListItem>
              ) : filteredGroups.length > 0 ? (
                filteredGroups.map((group, index) => (
                  <ListItem
                    button
                    key={group._id}
                    selected={index === selectedIndex}
                    onClick={() => handleSelectGroup(group.groupName)}
                    sx={{
                      py: 1,
                      borderBottom: "1px solid #f7f7f7",
                      transition: "background 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(30, 185, 216, 0.12)",
                        "&:hover": {
                          backgroundColor: "rgba(30, 185, 216, 0.2)",
                        },
                      },
                    }}
                    role="option"
                    aria-selected={index === selectedIndex}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {group.groupName}
                    </Typography>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <Typography variant="body2" color="textSecondary">
                    No groups found
                  </Typography>
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </MDBox> */}

      {/* New Group Button */}
      <Button
        variant="contained"
        onClick={() => setOpenCreate(true)}
        startIcon={<AddIcon />}
        sx={{
          backgroundColor: "#fb8c00",
          textTransform: "none",
          color: "white !important",
          ml: 2,
          borderRadius: "10px",
          fontWeight: 600,
          fontSize: "0.95rem",
          px: 2.5,
          py: 1.2,
          letterSpacing: "0.3px",
          boxShadow: "0 4px 8px rgba(30,185,216,0.2)",
          "&:hover": {
            backgroundColor: "#fb8c00",
            boxShadow: "0 6px 12px rgba(30,185,216,0.25)",
          },
          transition: "all 0.25s ease",
        }}
      >
        New Group
      </Button>
      <CreateGroupDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSuccess={() => dispatch(fetchGroups())}
      />
    </MDBox>
  );
}

export default FilterCard;
